import * as React from 'react';
import { FComponent } from '../../types/common';
import { setCSSVar } from '../../utils';
import { useAnimate } from 'framer-motion';
import withErrorBoundary from '../../hoc/withErrorBoundary';
import { logger } from '../../lib/logger';

const log = logger('SimpleProgressBar');

type SimpleProgressBarProps = {
  height?: string;
  progress: number; // 10 for 10%
  color?: string;
  scaleFactor?: number;
};

// How much the bar grows when score change animation is underway
const SCALE_FACTOR = 1.2;

const SimpleProgressBarComponent: FComponent<SimpleProgressBarProps> = ({
  height = '25px',
  progress,
  color = 'var(--colors-primary-400)',
  scaleFactor = SCALE_FACTOR
}) => {
  if (isNaN(progress)) {
    throw new Error('Progress must be a number');
  }
  const [scope, animate] = useAnimate();

  React.useEffect(() => {
    const animateProgress = async () => {
      await animate(
        scope.current,
        { width: `${progress}%`, scaleY: scaleFactor },
        { duration: 0.5 }
      );
      await animate(scope.current, { scaleY: 1 }, { duration: 0.5, type: 'spring' });
    };
    animateProgress().catch(e =>
      log.exception({
        error: e || 'Failed to animate progress bar.',
        tags: { progress, scaleFactor }
      })
    );
  }, [animate, progress, scaleFactor, scope]);

  return (
    <div className="level-progress-bar-frame" style={setCSSVar('--bar-height', height)}>
      <div
        ref={scope}
        className="level-progress-bar"
        style={{ ...setCSSVar('--bar-color', color) }}
      />
    </div>
  );
};

const SimpleProgressBar = withErrorBoundary(
  SimpleProgressBarComponent,
  'SimpleProgressBar'
);

export { SimpleProgressBar };
